import type { TUserGroup, TUserRank } from "MVVM/@types";
import type { TStatTone } from "MVVM/Views/Match/styledComponents";
import type {
  TMatchPageMoment,
  TMatchPagePlayer,
  TPlayerMatchStats,
} from "MVVM/Views/Match/@types";

// Round rides along in clipTags rather than its own field, same as the match
// history moment list reads it.
export const momentRound = (moment: TMatchPageMoment) => {
  const tag = moment.clipTags?.find((entry) => entry.key === "CS_Round");
  return tag ? Number(tag.value) : undefined;
};

export const momentTitle = (moment: TMatchPageMoment) =>
  moment.title || moment.timelineLabel || "Moment";

export const formatDuration = (seconds?: number | null) => {
  if (!seconds) return undefined;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${`${whole % 60}`.padStart(2, "0")}`;
};

export type TMomentState = "ready" | "rendering" | "failed" | "idle";

// One place decides what a moment is doing, so the tile, the counts and the
// poll can never disagree about it.
export const momentState = (
  moment: TMatchPageMoment,
  requested: string[],
): TMomentState => {
  if (moment.clip?.url) return "ready";
  const status = moment.clip?.status?.toUpperCase();
  if (status === "ERRORED" || status === "ERROR") return "failed";
  if (moment.clip || requested.includes(moment.id)) return "rendering";
  return "idle";
};

export type TMomentSort = "ready" | "best" | "round";

// Ready first: the page is about watching highlights, so what exists to watch
// leads. Everything else falls back to the score the backend already ranked by.
export const sortMoments = (
  moments: TMatchPageMoment[],
  requested: string[],
) => {
  const rank: Record<TMomentState, number> = {
    ready: 0,
    rendering: 1,
    idle: 2,
    failed: 3,
  };
  return [...moments].sort((a, b) => {
    const byState =
      rank[momentState(a, requested)] - rank[momentState(b, requested)];
    return byState !== 0 ? byState : (b.score ?? 0) - (a.score ?? 0);
  });
};

// The weapon the moment was made with. Lives in clipTags next to the round,
// same tag the clip badges read.
export const momentWeapon = (moment: TMatchPageMoment) =>
  moment.clipTags?.find((entry) => entry.key === "CS_Weapons")?.value;

// A column reads top to bottom, so the order is the ranking and the viewer gets
// to choose which ranking it is.
export const sortMomentsBy = (
  moments: TMatchPageMoment[],
  requested: string[],
  mode: TMomentSort,
) => {
  if (mode === "best") {
    return [...moments].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }
  if (mode === "round") {
    return [...moments].sort(
      (a, b) => (momentRound(a) ?? 0) - (momentRound(b) ?? 0),
    );
  }
  return sortMoments(moments, requested);
};

// Five bands, shared so the scoreboard, the performance tab and the top-three
// strip cannot disagree about what a good number looks like. Envy is scarce and
// only the top band earns it; lime carries the merely good; yellow marks only
// the bottom band, so it still means something when it appears.
export const toneFor = (
  value: number | null | undefined,
  elite: number,
  strong: number,
  even: number,
  weak: number,
): TStatTone => {
  if (value === null || value === undefined) return "even";
  if (value >= elite) return "elite";
  if (value >= strong) return "strong";
  if (value >= even) return "even";
  if (value >= weak) return "weak";
  return "poor";
};

export const kdRatio = (row: Pick<TPlayerMatchStats, "kills" | "deaths">) =>
  row.deaths === 0 ? row.kills : row.kills / row.deaths;

// One rule for who a player is, everywhere on this page. An Allstar account is
// the identity they chose, so it wins over the Steam persona when they have
// one; the Steam name stays available as the secondary label rather than being
// thrown away, because it is the name their teammates called them in game.
export const playerIdentity = (
  player: TMatchPagePlayer | undefined,
  fallback: string,
) => {
  const steamName = player?.displayName || undefined;
  const allstarName = player?.user?.username || undefined;
  return {
    name: allstarName || steamName || fallback,
    // Only worth showing when it differs from the name above it.
    alt: allstarName && steamName !== allstarName ? steamName : undefined,
    avatar: player?.user?.avatarUrl || player?.avatar || undefined,
    isAllstar: !!allstarName,
    /* Undefined for unclaimed players, which is what short-circuits the
       hover profile card: AvatarV2 only mounts ProfileCardWrapper when it
       gets a username, so a Steam account with no Allstar profile has no
       hover target rather than an empty card. */
    username: allstarName,
    // Fed straight to AvatarV2, which decides the border and badge from these
    // two. FREE is its own default and draws nothing.
    userGroup: (player?.user?.userGroup?.type || undefined) as
      | TUserGroup
      | undefined,
    rank: (player?.user?.rank || undefined) as TUserRank | undefined,
  };
};

export type TLobbyRow = { moment: TMatchPageMoment; gameId: string };

// Every player's best few, flattened. Shared so the Overview row and the tab
// rank the same set rather than each deriving its own.
export const lobbyRows = (
  players: { gameId: string }[],
  momentsByPlayer: Record<string, { moments: TMatchPageMoment[] }>,
): TLobbyRow[] => {
  const rows: TLobbyRow[] = [];
  players.forEach((player) => {
    (momentsByPlayer[player.gameId]?.moments || []).forEach((moment) => {
      rows.push({ moment, gameId: player.gameId });
    });
  });
  return rows;
};

// You, then the people you played with, then the people you played against.
// The scoreboard's order, so the two surfaces agree.
export const orderedBySide = <T extends { gameId: string }>(
  players: T[],
  viewerGameId: string,
  teamOf: Record<string, string | undefined>,
) => {
  const viewerTeam = teamOf[viewerGameId];
  const weight = (player: T) => {
    if (player.gameId === viewerGameId) return 0;
    if (viewerTeam && teamOf[player.gameId] === viewerTeam) return 1;
    return 2;
  };
  return [...players].sort((a, b) => weight(a) - weight(b));
};

// The lobby ranking for the All Highlights top row: what already exists to
// watch leads, then your own side, then the parser's score. Teammates get the
// boost because they are who you actually share and create for.
export const rankLobbyMoments = <T extends { moment: TMatchPageMoment }>(
  rows: T[],
  requested: string[],
  isTeammate: (row: T) => boolean,
) => {
  const stateRank: Record<TMomentState, number> = {
    ready: 0,
    rendering: 1,
    idle: 2,
    failed: 3,
  };
  return [...rows].sort((a, b) => {
    const byState =
      stateRank[momentState(a.moment, requested)] -
      stateRank[momentState(b.moment, requested)];
    if (byState !== 0) return byState;
    const byTeam = Number(isTeammate(b)) - Number(isTeammate(a));
    if (byTeam !== 0) return byTeam;
    return (b.moment.score ?? 0) - (a.moment.score ?? 0);
  });
};
